import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import api from "../../api/axios";
import endpoints from "../../api/endpoints";
import { handleApiError } from "../../pages/UBTAdmin/ProjectDetails/ProjectHelper/ViewProjectHandleApiError";

export interface PipItem {
  [key: string]: any;
}

export interface PipListState {
  projectList: PipItem[];
  projectListLoading: boolean;
  projectListError: string | null;
  projectListCurrentPage: number;
  projectListPerPage: number;
  projectListTotalItems: number;
  projectListTotalPages: number;
}

const initialState: PipListState = {
  projectList: [],
  projectListLoading: false,
  projectListError: null,
  projectListCurrentPage: 1,
  projectListPerPage: 5,
  projectListTotalItems: 0,
  projectListTotalPages: 0,
};



export const fetchPipList = createAsyncThunk(
  "ubtPortal/fetchPipList",
  async (
    {
      page,
      perPage,
      sortType,
      sortOrder,
      teamId,
      nuid,
      pipId,
    }: {
      page: number;
      perPage: number;
      sortType?: string;
      sortOrder?: string;
      teamId?: number | string | null;
      nuid?: string | null;
      pipId?: string | number | null;
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const options: any = {
        signal,
        params: {
          page,
          per_page: perPage,
          sortType,
          sortOrder,
          ...(pipId === undefined ? {} : { pipId }),
        },
        headers: {
          accept: "application/json",
          ...(nuid ? { "x-nuid": nuid } : {}),
        },
      };
      const url =
        typeof endpoints.ubt.projectList.getpipList === "function"
          ? endpoints.ubt.projectList.getpipList(teamId ?? undefined)
          : endpoints.ubt.projectList.getpipList;
      const response = await api.get(url, options);
      return response.data;
    } catch (error: any) {
      return handleApiError(error, rejectWithValue, "Failed to fetch PIP list");
    }
  },
);

const pipListSlice = createSlice({
  name: "pipList",
  initialState,
  reducers: {
    resetPipListState: (state) => {
      state.projectList = [];
      state.projectListLoading = false;
      state.projectListError = null;
      state.projectListCurrentPage = 1;
      state.projectListTotalItems = 0;
      state.projectListTotalPages = 0;
    },
    setProjectDetailCurrentPage: (state, action: PayloadAction<number>) => {
      state.projectListCurrentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPipList.pending, (state) => {
      state.projectListLoading = true;
      state.projectListError = null;
    });

    builder.addCase(fetchPipList.fulfilled, (state, action) => {
      state.projectListLoading = false;
      const payload: any = action.payload;

      // Support both array responses and paged { items, meta } responses
      if (payload?.items && Array.isArray(payload.items)) {
        state.projectList = payload.items;
        state.projectListTotalItems = payload.meta?.total ?? state.projectListTotalItems;
        state.projectListTotalPages = payload.meta?.totalPages ?? state.projectListTotalPages;
        state.projectListCurrentPage = payload.meta?.pageNumber ?? state.projectListCurrentPage;
        state.projectListPerPage = payload.meta?.pageSize ?? state.projectListPerPage;
      } else if (Array.isArray(payload)) {
        state.projectList = payload;
        state.projectListTotalItems = payload.length;
        state.projectListTotalPages = 1;
        state.projectListCurrentPage = 1;
      } else if (payload?.data && Array.isArray(payload.data)) {
        // some APIs return { data: [...] }
        state.projectList = payload.data;
        state.projectListTotalItems = payload.total ?? state.projectListTotalItems;
      } else {
        state.projectList = [];
      }
    });

    builder.addCase(fetchPipList.rejected, (state, action) => {
      state.projectListLoading = false;
      state.projectListError = action.payload as string;
      // Keep UI consistent with server state when list fetch fails (e.g. no projects found).
      state.projectList = [];
      state.projectListTotalItems = 0;
      state.projectListTotalPages = 0;
    });
  },
});

export const { resetPipListState, setProjectDetailCurrentPage } = pipListSlice.actions;
export default pipListSlice.reducer;
